import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import type { MarketingResource } from "creso-core/types";
import { getCORSSafeURL } from "@/utils/url";
import { captureWebStoryEndCardImage } from "./capture-web-story-end-card";
import type { WebStoryBrandingDetails } from "../components/web-story-end-card";

const MAX_FRAME_WIDTH = 720;
const MAX_FRAME_HEIGHT = 1280;
const SECONDS_PER_IMAGE = 5;
const FPS = 30;

/** No-op: no WASM to preload anymore */
export function preloadWebStoryFFmpeg() {}

export function getWebStoryImageAttachments(item: MarketingResource) {
  return (
    item.attachments?.filter(
      (attachment) => attachment.attachment_type !== "video"
    ) ?? []
  );
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = getCORSSafeURL(url);
  });
}

function getScaledDimensions(image: HTMLImageElement) {
  const scale = Math.min(
    MAX_FRAME_WIDTH / image.naturalWidth,
    MAX_FRAME_HEIGHT / image.naturalHeight,
    1
  );
  // libx264 / AVC requires dimensions divisible by 2
  const width = Math.max(2, Math.floor((image.naturalWidth * scale) / 2) * 2);
  const height = Math.max(2, Math.floor((image.naturalHeight * scale) / 2) * 2);
  return { width, height };
}

type VideoFrameSource = HTMLImageElement;

export async function generateWebStoryVideoFromAttachments(
  imageUrls: string[],
  endCardDetails?: WebStoryBrandingDetails
): Promise<Blob> {
  if (imageUrls.length === 0) {
    throw new Error("No images available to generate video");
  }

  const storyImages = await Promise.all(imageUrls.map(loadImage));
  const { width, height } = getScaledDimensions(storyImages[0]);

  const frameSources: VideoFrameSource[] = [...storyImages];

  if (endCardDetails) {
    const endCardImage = await captureWebStoryEndCardImage(
      endCardDetails,
      width,
      height
    );
    frameSources.push(endCardImage);
  }

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: "avc", width, height },
    fastStart: "in-memory",
  });

  const frameDurationMicros = 1_000_000 / FPS;

  const videoEncoder = new VideoEncoder({
    output: (chunk, meta) => {
      // Safari: chunk.duration is null, must always pass duration explicitly
      const buf = new Uint8Array(chunk.byteLength);
      chunk.copyTo(buf);
      muxer.addVideoChunkRaw(
        buf,
        chunk.type,
        chunk.timestamp,
        frameDurationMicros,
        meta
      );
    },
    error: (e) => {
      throw e;
    },
  });

  videoEncoder.configure({
    codec: "avc1.42001f",
    width,
    height,
    bitrate: 2_000_000,
    framerate: FPS,
  });

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  const framesPerImage = SECONDS_PER_IMAGE * FPS;

  for (let i = 0; i < frameSources.length; i += 1) {
    ctx.drawImage(frameSources[i], 0, 0, width, height);

    for (let f = 0; f < framesPerImage; f += 1) {
      const timestampMicros = (i * framesPerImage + f) * frameDurationMicros;
      const frame = new VideoFrame(canvas, { timestamp: timestampMicros });
      videoEncoder.encode(frame, { keyFrame: f === 0 });
      frame.close();
    }
  }

  await videoEncoder.flush();
  muxer.finalize();

  const { buffer } = muxer.target;
  return new Blob([buffer], { type: "video/mp4" });
}
